import * as React from "react";
import { useFloating, flip, shift, size, offset, autoUpdate, FloatingPortal, useMergeRefs } from "@floating-ui/react";
import ReactSelect, { components } from "react-select";
import { Transition } from "react-transition-group";
import { AnalyticsTrigger } from "../../helpers/analytics/analytics-trigger/analytics-trigger.mjs";
import { Check } from "../check/check.mjs";
import { FormHelper } from "../form-helper/form-helper.mjs";
import { HeaderContext } from "../../helpers/header-provider/header-provider.mjs";
import "debounce";
import "tabbable";
import { useIsMounted } from "../../helpers/helper/hooks/use-is-mounted.mjs";
import { Icon } from "../../icon/icon/icon.mjs";
import { Image } from "../../image/image.mjs";
import { useLabels } from "../../helpers/label-provider/use-labels.mjs";
import { PropsChecker } from "../../helpers/props-checker/props-checker.mjs";
import { useTheme } from "../../helpers/theme-provider/use-theme.mjs";
const SelectContext = React.createContext({
  live: false,
  employee: false,
  multiple: false,
  isOpen: false,
  size: void 0,
  inputValue: "",
  setFloatingReference: () => null
});
const ValueContainer = (props) => {
  const { children, ...rest } = props;
  const { inputValue } = React.useContext(SelectContext);
  const childrenArray = React.Children.toArray(children);
  const inputChild = childrenArray[childrenArray.length - 1];
  const values = childrenArray.filter((child) => child !== inputChild);
  const input = React.isValidElement(inputChild) ? inputChild : null;
  return /* @__PURE__ */ React.createElement(components.ValueContainer, { ...rest }, !inputValue && /* @__PURE__ */ React.createElement("div", { className: "select__value-wrapper" }, values), input ? React.cloneElement(input, {
    // replace aria-readonly with readOnly, as some screen readers announce combobox with aria-readonly as "read only", which is not correct.
    "aria-readonly": void 0,
    readOnly: (input == null ? void 0 : input.props["aria-readonly"]) === true
  }) : inputChild);
};
const MultiValueLabel = (props) => {
  return /* @__PURE__ */ React.createElement(React.Fragment, null, props.children);
};
const DropdownIndicator = () => {
  const { live } = React.useContext(SelectContext);
  if (live) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Icon, { modifiers: "#search", className: "select__icon-search" }), /* @__PURE__ */ React.createElement(Icon, { modifiers: "#arrow-down", className: "select__icon" }));
  }
  return /* @__PURE__ */ React.createElement(Icon, { modifiers: "#arrow-down", className: "select__icon" });
};
const Menu = (props) => {
  const { isOpen, size: size2, employee, setFloatingReference } = React.useContext(SelectContext);
  const menuContainerBEM = (transitionState) => {
    const classArray = ["select-menu-outer"];
    if (size2) {
      classArray.push("select-menu-outer--" + size2);
    }
    if (employee) {
      classArray.push("select-menu-outer--employee");
    }
    if (transitionState) {
      classArray.push(transitionState);
    }
    return classArray.join(" ");
  };
  const ref = useMergeRefs([setFloatingReference, props.innerRef]);
  return /* @__PURE__ */ React.createElement(Transition, { in: isOpen, timeout: { enter: 10, exit: 100 }, mountOnEnter: true, unmountOnExit: true }, (state) => /* @__PURE__ */ React.createElement(components.Menu, { ...props, innerRef: ref, className: menuContainerBEM(state) }, props.children));
};
const MenuList = (props) => {
  return /* @__PURE__ */ React.createElement(components.MenuList, { ...props }, props.children);
};
const OptionAsterisk = () => {
  return /* @__PURE__ */ React.createElement("div", { className: "select__asterisk-wrapper" }, /* @__PURE__ */ React.createElement(Icon, { modifiers: "#star", className: "select__asterisk" }));
};
const OptionIcon = (props) => {
  const { icon } = props;
  return /* @__PURE__ */ React.createElement("div", { className: "select__option-icon-wrapper" }, /* @__PURE__ */ React.createElement(Icon, { modifiers: icon, className: "select__option-icon" }));
};
const OptionImage = (props) => {
  const { image } = props;
  return /* @__PURE__ */ React.createElement("div", { className: "select__image-wrapper" }, /* @__PURE__ */ React.createElement(Image, { src: image.src, ...image, className: "select__image" }));
};
const OptionDisplayedLabel = ({ children, label }) => {
  if (children) {
    return /* @__PURE__ */ React.createElement("div", { className: "select__option-children" }, children);
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, label);
};
const SubLabel = ({ subLabel }) => {
  return /* @__PURE__ */ React.createElement("div", { className: "select__sub-label" }, subLabel);
};
const ImageOptionContent = (props) => {
  const option = props.data;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, option.image && /* @__PURE__ */ React.createElement(OptionImage, { image: option.image }), /* @__PURE__ */ React.createElement("div", { className: "select__image-option" }, /* @__PURE__ */ React.createElement(OptionDisplayedLabel, { ...option }), option.showAsterisk && /* @__PURE__ */ React.createElement(OptionAsterisk, null)));
};
const IconOptionContent = (props) => {
  const option = props.data;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, option.icon && /* @__PURE__ */ React.createElement(OptionIcon, { icon: option.icon }), /* @__PURE__ */ React.createElement("div", { className: "select__option-icon-label" }, /* @__PURE__ */ React.createElement(OptionDisplayedLabel, { ...option }), option.showAsterisk && /* @__PURE__ */ React.createElement(OptionAsterisk, null)));
};
const DefaultOptionContent = (props) => {
  const option = props.data;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(OptionDisplayedLabel, { ...option }), option.showAsterisk && /* @__PURE__ */ React.createElement(OptionAsterisk, null));
};
const OptionContent = (props) => {
  const option = props.data;
  if (option.image) {
    return /* @__PURE__ */ React.createElement(ImageOptionContent, { ...props });
  } else if (option.icon) {
    return /* @__PURE__ */ React.createElement(IconOptionContent, { ...props });
  } else {
    return /* @__PURE__ */ React.createElement(DefaultOptionContent, { ...props });
  }
};
const MultipleOptionContent = (props) => {
  const option = props.data;
  return /* @__PURE__ */ React.createElement("div", { className: "select__multi-option-content" }, /* @__PURE__ */ React.createElement(OptionContent, { ...props }), option.subLabel && /* @__PURE__ */ React.createElement(SubLabel, { subLabel: option.subLabel }));
};
const MultiValue = (props) => {
  const value = props.data;
  return /* @__PURE__ */ React.createElement(components.MultiValue, { ...props }, value.icon && /* @__PURE__ */ React.createElement(OptionIcon, { icon: value.icon }), value.image && /* @__PURE__ */ React.createElement(OptionImage, { image: value.image }), props.children, value.showAsterisk && /* @__PURE__ */ React.createElement(OptionAsterisk, null));
};
const Option = (props, checkProps) => {
  const { employee, multiple, size: size2 } = React.useContext(SelectContext);
  const option = props.data;
  const employeeOption = option.employee && !employee;
  const optionBEM = () => {
    if (employeeOption) {
      return "select__option--employee";
    }
  };
  if (multiple) {
    return /* @__PURE__ */ React.createElement(components.Option, { ...props, className: optionBEM() }, /* @__PURE__ */ React.createElement(
      Check,
      {
        ...checkProps,
        onChange: () => {
          var _a, _b;
          (_b = (_a = props.innerProps).onClick) == null ? void 0 : _b.call(_a);
        },
        size: size2 === "small" ? "small" : void 0,
        checked: props.isSelected,
        disabled: props.isDisabled,
        employee: employeeOption,
        className: "select__option-check"
      },
      /* @__PURE__ */ React.createElement(MultipleOptionContent, { ...props })
    ));
  }
  return /* @__PURE__ */ React.createElement(components.Option, { ...props, className: optionBEM() }, /* @__PURE__ */ React.createElement(OptionContent, { ...props }), option.subLabel && /* @__PURE__ */ React.createElement(SubLabel, { subLabel: option.subLabel }));
};
const SingleValue = (props) => {
  const value = props.data;
  return /* @__PURE__ */ React.createElement(components.SingleValue, { ...props }, value.icon && /* @__PURE__ */ React.createElement(OptionIcon, { icon: value.icon }), value.image && /* @__PURE__ */ React.createElement(OptionImage, { image: value.image }), /* @__PURE__ */ React.createElement("div", { className: "select__text-wrapper" }, value.children || value.label), value.showAsterisk && /* @__PURE__ */ React.createElement(OptionAsterisk, null));
};
const ClearIndicator = () => null;
const IndicatorSeparator = () => null;
const MultiValueRemove = () => null;
const Select = (props) => {
  return /* @__PURE__ */ React.createElement(
    PropsChecker,
    {
      componentName: "Select",
      componentProps: props,
      defaultValueName: "defaultValue",
      valueName: "value",
      onChangeNames: "onChange",
      isComponentDisabled: props.disabled
    },
    /* @__PURE__ */ React.createElement(
      AnalyticsTrigger,
      {
        trigger: [
          {
            callback: "onShow",
            id: "Select - Open - ".concat(props.id),
            elementId: "".concat(props.id),
            elementType: "select",
            interactionType: "open"
          },
          {
            callback: "onHide",
            id: "Select - Close - ".concat(props.id),
            elementId: "".concat(props.id),
            elementType: "select",
            interactionType: "close"
          },
          {
            callback: "onChange",
            id: "Select - Choose - ".concat(props.id),
            elementId: "".concat(props.id),
            elementType: "select",
            interactionType: "select"
          }
        ]
      },
      /* @__PURE__ */ React.createElement(SelectInner, { ...props })
    )
  );
};
const SelectInner = (props) => {
  const {
    className,
    defaultValue,
    disabled,
    elementRef,
    employee,
    helpMessage,
    errorMessage,
    successMessage,
    employeeMessage,
    hideLabel,
    id,
    isInvalid,
    label,
    live = false,
    multiple,
    name,
    noResultsText,
    onChange,
    onHide,
    onShow,
    options,
    placeholder = "",
    portalTarget,
    simpleValue,
    size: size$1,
    style,
    value,
    required
  } = props;
  const isValueArray = (value2) => {
    return Array.isArray(value2);
  };
  const [isOpen, setIsOpen] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const isMounted = useIsMounted();
  const labels = useLabels();
  const [inputValue, setInputValue] = React.useState("");
  const { headerHeight, isHeaderHidden, headerBottomHeight } = React.useContext(HeaderContext);
  const padding = {
    top: isHeaderHidden ? (
      // employee header is always visible
      headerHeight - headerBottomHeight + 8
    ) : headerHeight + 8,
    right: 0,
    bottom: 8,
    left: 0
  };
  const floatingContext = useFloating({
    placement: "bottom-start",
    middleware: [
      flip({ padding }),
      shift(),
      size({
        padding,
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            width: "".concat(rects.reference.width, "px"),
            maxHeight: "".concat(Math.max(100, availableHeight), "px")
          });
        }
      }),
      offset({
        mainAxis: -1
      })
    ],
    whileElementsMounted: autoUpdate,
    open: isOpen
  });
  const { refs, floatingStyles } = floatingContext;
  const { setFloating: setFloatingReference } = refs;
  const getValue = () => {
    return typeof value !== "undefined" ? findValueFromOptions(value, options) : values;
  };
  const findSimpleValueFromOptions = (value2, options2) => {
    if (Array.isArray(value2)) {
      return options2.filter((object) => value2.find((option) => option === object.value));
    } else {
      return options2.find((option) => option.value === value2) || null;
    }
  };
  const findDefaultValueFromOptions = (value2, options2) => {
    if (!value2) {
      return null;
    }
    if (isValueArray(value2)) {
      return options2.filter((object) => value2.find((option) => option.value === object.value));
    } else {
      return options2.find((option) => option.value === value2.value) || null;
    }
  };
  const findValueFromOptions = (value2, options2) => {
    if (simpleValue && typeof value2 === "string" || Array.isArray(value2) && typeof value2[0] === "string") {
      return findSimpleValueFromOptions(value2, options2);
    } else {
      return findDefaultValueFromOptions(value2, options2);
    }
  };
  const [values, setValues] = React.useState(findValueFromOptions(defaultValue, options));
  const nativeSelectValue = () => {
    if (!values) {
      return void 0;
    }
    if (multiple) {
      if (Array.isArray(values)) {
        return values.map((values2) => values2.value);
      } else {
        return [values.value];
      }
    }
    if (Array.isArray(values)) {
      return values.map((values2) => values2.value);
    }
    return values.value;
  };
  const getSimpleValue = (value2) => {
    if (!value2) {
      return null;
    }
    if (isValueArray(value2)) {
      return value2.map((value3) => value3.value);
    } else {
      return value2.value;
    }
  };
  const handleChange = (values2) => {
    if (typeof value === "undefined") {
      setValues(values2);
    }
    if (onChange && !simpleValue) {
      onChange(values2);
    }
    if (onChange && simpleValue) {
      onChange(getSimpleValue(values2));
    }
  };
  const getContent = () => {
    if (!props.contentRenderer) {
      return null;
    }
    return props.contentRenderer();
  };
  const BEM = () => {
    const classArray = ["select"];
    if (disabled) {
      classArray.push("is-disabled");
    }
    if (isInvalid != null ? isInvalid : !!errorMessage) {
      classArray.push("is-invalid");
    }
    if (isOpen) {
      classArray.push("is-open");
    }
    if (isFocused) {
      classArray.push("is-focused");
    }
    if (size$1) {
      classArray.push("select--".concat(size$1));
    }
    if (style) {
      classArray.push("select--".concat(style));
    }
    if (employee) {
      classArray.push("select--employee");
    }
    if (live) {
      classArray.push("select--searchable");
    }
    if (hideLabel) {
      classArray.push("select--no-label-".concat(hideLabel));
    }
    if (className) {
      classArray.push(className);
    }
    return classArray.join(" ");
  };
  const onMenuOpen = () => {
    if (!isOpen) {
      setIsOpen(true);
    }
    if (onShow) {
      onShow();
    }
  };
  const onMenuClose = () => {
    if (isOpen) {
      setIsOpen(false);
      if (onHide) {
        onHide();
      }
    }
  };
  const onFocus = () => {
    if (isFocused) {
      return;
    }
    setIsFocused(true);
  };
  const onBlur = () => {
    if (!isFocused) {
      return;
    }
    setIsFocused(false);
  };
  const getNoResultsText = () => {
    return noResultsText != null ? noResultsText : labels.get("select.noResultsFound");
  };
  const getAssistiveText = ({ count }) => {
    switch (count) {
      case 0:
        return noResultsText != null ? noResultsText : labels.get("select.noResultsFound");
      case 1:
        return labels.get("select.oneResultFound");
      default:
        return labels.get("select.resultsFound", {
          count: count.toString()
        });
    }
  };
  const { portalRoot } = useTheme();
  const selectContextValue = React.useMemo(
    () => ({
      live,
      employee,
      multiple,
      isOpen,
      size: size$1,
      inputValue,
      setFloatingReference
    }),
    [live, employee, multiple, isOpen, size$1, inputValue, setFloatingReference]
  );
  const handleKeyDown = React.useCallback(
    (event) => {
      if (isOpen && event.key === "Escape") {
        event.stopPropagation();
      }
    },
    [isOpen]
  );
  const MenuPortal = React.useCallback(
    ({ children }) => {
      return /* @__PURE__ */ React.createElement(FloatingPortal, { root: typeof portalTarget !== "undefined" ? portalTarget : portalRoot }, children);
    },
    [portalTarget, portalRoot]
  );
  const renderReactSelect = () => {
    const customComponents = {
      ClearIndicator,
      DropdownIndicator,
      IndicatorSeparator,
      Menu,
      MenuList,
      MultiValue,
      MultiValueLabel,
      MultiValueRemove,
      Option,
      SingleValue,
      ValueContainer,
      MenuPortal
    };
    const styles = {
      container: () => ({}),
      control: () => ({}),
      indicatorsContainer: () => ({}),
      input: () => ({}),
      menu: () => ({ ...floatingStyles }),
      menuList: () => ({}),
      menuPortal: () => ({}),
      multiValue: () => ({}),
      multiValueLabel: () => ({}),
      multiValueRemove: () => ({}),
      noOptionsMessage: () => ({}),
      option: () => ({}),
      placeholder: () => ({}),
      singleValue: () => ({}),
      valueContainer: () => ({})
    };
    return /* @__PURE__ */ React.createElement(SelectContext.Provider, { value: selectContextValue }, /* @__PURE__ */ React.createElement(
      ReactSelect,
      {
        id,
        instanceId: id,
        inputId: "".concat(id, "-input"),
        inputValue,
        onInputChange: setInputValue,
        "aria-labelledby": "".concat(id, "-label"),
        className: "select__wrapper",
        name,
        options,
        value: getValue(),
        onChange: handleChange,
        classNamePrefix: "select",
        components: customComponents,
        onMenuOpen,
        onMenuClose,
        onFocus,
        onBlur,
        menuIsOpen: isOpen,
        styles,
        isMulti: multiple,
        closeMenuOnSelect: !multiple,
        hideSelectedOptions: false,
        isDisabled: disabled,
        placeholder: placeholder || "",
        menuPortalTarget: typeof window !== "undefined" ? document.body : void 0,
        isClearable: live,
        noOptionsMessage: getNoResultsText,
        backspaceRemovesValue: false,
        menuShouldScrollIntoView: false,
        isSearchable: live,
        required,
        screenReaderStatus: (args) => getAssistiveText(args),
        onKeyDown: handleKeyDown
      }
    ));
  };
  const renderNativeSelect = () => {
    return /* @__PURE__ */ React.createElement("div", { className: "select__wrapper" }, /* @__PURE__ */ React.createElement("div", { className: "select__control" }, /* @__PURE__ */ React.createElement("div", { className: "select__value-container" }, /* @__PURE__ */ React.createElement("select", { id: "".concat(id, "-input"), name, className: "select__native-select", disabled, multiple, defaultValue: nativeSelectValue() }, options.map((option) => {
      return /* @__PURE__ */ React.createElement("option", { key: option.value, value: option.value }, option.label);
    })))));
  };
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      className: BEM(),
      ref: elementRef,
      onClick: (e) => e.stopPropagation()
    },
    /* @__PURE__ */ React.createElement("label", { className: "select__label", id: "".concat(id, "-label"), htmlFor: "".concat(id, "-input"), "aria-hidden": isMounted }, employee && /* @__PURE__ */ React.createElement(Icon, { modifiers: "#support", className: "textfield__label-icon" }), label),
    helpMessage && /* @__PURE__ */ React.createElement(FormHelper, { text: helpMessage, type: "help", id: "".concat(id, "-help-message"), className: "select__form-helper" }),
    errorMessage && /* @__PURE__ */ React.createElement(FormHelper, { text: errorMessage, type: "error", id: "".concat(id, "-error-message"), className: "select__form-helper" }),
    successMessage && /* @__PURE__ */ React.createElement(FormHelper, { text: successMessage, type: "valid", id: "".concat(id, "-success-message"), className: "select__form-helper" }),
    employeeMessage && /* @__PURE__ */ React.createElement(FormHelper, { text: employeeMessage, type: "employee", id: "".concat(id, "-employee-message"), className: "select__form-helper" }),
    /* @__PURE__ */ React.createElement("div", { className: "select__inner", ref: refs.setReference }, isMounted ? renderReactSelect() : renderNativeSelect(), getContent())
  );
};
export {
  Select,
  SelectInner
};
